/**
 * Claim view model for the Billing screen claims table.
 *
 * Shaped after the FHIR Claim resource
 * (https://www.medplum.com/docs/api/fhir/resources/claim) so it can be swapped
 * for live Medplum data later with minimal changes.
 *
 * A single Claim can bill for multiple encounters; each encounter contributes
 * one or more line items (Claim.item, where Claim.item.encounter references the
 * encounter the service was rendered in).
 */

/** Claim.status — resource lifecycle status. */
export type ClaimStatus = 'active' | 'cancelled' | 'draft' | 'entered-in-error';

/** Claim.use — the nature of the request. */
export type ClaimUse = 'claim' | 'preauthorization' | 'predetermination';

/** Revenue-cycle status — separate from FHIR Claim.status. */
export type ClaimRcmStatus =
  | 'ready'
  | 'submitted'
  | 'acknowledged'
  | 'adjudicated'
  | 'denied'
  | 'rejected';

export interface ClaimReadinessCheck {
  label: string;
  passed: boolean;
}

export interface ClaimTimelineEvent {
  label: string;
  at: string;
  outcome: 'complete' | 'error' | 'pending';
  detail?: string;
}

/** Flat view model for one Claim.item line (Claim.item.productOrService + net). */
export interface ClaimLineItem {
  /** Claim.item.productOrService coding (CPT/HCPCS). */
  code: string;
  /** Claim.item.productOrService display. */
  description: string;
  /** Claim.item.quantity. */
  quantity: number;
  /** Claim.item.unitPrice amount (USD). */
  unitPrice: number;
  /** Claim.item.net amount (USD). */
  net: number;
}

/** Encounter-level grouping of Claim.item lines (Claim.item.encounter). */
export interface ClaimEncounter {
  /** Encounter.identifier the line items were rendered in. */
  encounterIdentifier: string;
  /** Appointment id behind the encounter, for cross-linking. */
  appointmentId: string;
  /** Date of service for this encounter (ISO). */
  serviceDate: string;
  /** Claim.item lines billed for this encounter. */
  items: ClaimLineItem[];
}

/** Flat, searchable/filterable view model for one Claim row. */
export interface ClaimRow {
  id: string;
  /** Claim.identifier */
  identifier: string;
  /** Claim.patient (Patient) display */
  patientName: string;
  /** Claim.provider (Practitioner) display */
  provider: string;
  /** Claim.status */
  status: ClaimStatus;
  /** Claim.type display */
  type: string;
  /** Claim.use */
  use: ClaimUse;
  /** Claim.insurer (Organization) display */
  payer: string;
  /** Claim.created (ISO) */
  created: string;
  /** Claim.meta.lastUpdated (ISO) */
  lastUpdatedOn: string;
  /** Display name from Claim.meta.lastUpdatedBy (Practitioner or system actor) */
  lastUpdatedBy: string;
  /** Claim.billablePeriod.start (ISO) */
  billablePeriodStart: string;
  /** Claim.billablePeriod.end (ISO) */
  billablePeriodEnd: string;
  /** Encounters billed by this claim, each with its own line items. */
  encounters: ClaimEncounter[];
  /** Revenue-cycle lifecycle status (not FHIR Claim.status). */
  rcmStatus: ClaimRcmStatus;
  stediCorrelationId?: string;
  payerClaimNumber?: string;
  subscriberId?: string;
  diagnosisCodes?: string[];
  readinessChecks?: ClaimReadinessCheck[];
  timeline?: ClaimTimelineEvent[];
  eobId?: string;
  accountId?: string;
}

/** Sum of all line item net amounts across every encounter on a claim (Claim.total). */
export function claimTotal(claim: ClaimRow): number {
  return claim.encounters.reduce(
    (sum, enc) => sum + enc.items.reduce((s, item) => s + item.net, 0),
    0,
  );
}

/** Total number of line items across every encounter on a claim. */
export function claimLineItemCount(claim: ClaimRow): number {
  return claim.encounters.reduce((count, enc) => count + enc.items.length, 0);
}

/** Subtotal for a single encounter's line items. */
export function encounterSubtotal(enc: ClaimEncounter): number {
  return enc.items.reduce((sum, item) => sum + item.net, 0);
}

/**
 * Earliest and latest encounter service dates on a claim (ISO).
 * A claim can bill for multiple encounters, so this represents the span of service.
 */
export function claimServiceDateRange(claim: ClaimRow): { start: string; end: string } {
  const dates = claim.encounters
    .map((enc) => enc.serviceDate)
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));
  return { start: dates[0] ?? '', end: dates[dates.length - 1] ?? '' };
}

export const MOCK_CLAIMS: ClaimRow[] = [];

export function findClaimById(claimId: string | undefined): ClaimRow | undefined {
  if (!claimId) return undefined;
  return MOCK_CLAIMS.find((c) => c.id === claimId);
}
